import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Send, Share2, Copy, Loader2, CheckCircle2 } from 'lucide-react';
import api from '../../api/auth.api';

export const EquipmentDispatch: React.FC = () => {
  const navigate = useNavigate();
  const [inputText, setInputText] = useState('');
  const [formattedMessage, setFormattedMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [shared, setShared] = useState(false);

  const handleFormat = async () => {
    if (!inputText.trim()) return;
    setLoading(true);
    setError(null);
    setFormattedMessage('');
    setCopied(false);
    setShared(false);

    try {
      const response = await api.post('/api/dispatch/format', { text: inputText.trim() });
      if (response.data.success) {
        if (response.data.data.needs_clarification) {
          setError(response.data.data.message);
        } else {
          setFormattedMessage(response.data.data.formatted_message);
        }
      } else {
        setError(response.data.error || 'Failed to format dispatch');
      }
    } catch (err: any) {
      console.error('Dispatch format error:', err);
      setError(err.response?.data?.error || 'Failed to process dispatch');
    } finally {
      setLoading(false);
    }
  };

  const handleShare = async () => {
    if (!formattedMessage) return;

    if (navigator.share) {
      try {
        await navigator.share({ text: formattedMessage });
        setShared(true);
        setTimeout(() => setShared(false), 2000);
      } catch (err: any) {
        if (err.name !== 'AbortError') {
          handleCopy();
        }
      }
    } else {
      handleCopy();
    }
  };

  const handleCopy = async () => {
    if (!formattedMessage) return;
    try {
      await navigator.clipboard.writeText(formattedMessage);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = formattedMessage;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleClear = () => {
    setInputText('');
    setFormattedMessage('');
    setError(null);
    setCopied(false);
    setShared(false);
  };

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f8fafc' }}>
      {/* Header */}
      <div style={{
        background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
        padding: '16px 20px',
        display: 'flex',
        alignItems: 'center',
        gap: 12
      }}>
        <button
          onClick={() => navigate('/')}
          style={{ background: 'none', border: 'none', color: 'white', cursor: 'pointer', padding: 4 }}
        >
          <ArrowLeft size={24} />
        </button>
        <div>
          <h1 style={{ fontSize: 20, fontWeight: 700, color: 'white', margin: 0 }}>Equipment Dispatch</h1>
          <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.8)', margin: 0 }}>AI-powered move formatter</p>
        </div>
      </div>

      <div style={{ padding: 16 }}>
        {/* Input */}
        <div style={{ marginBottom: 16 }}>
          <label style={{ fontSize: 14, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 6 }}>
            Describe the move
          </label>
          <textarea
            value={inputText}
            onChange={e => setInputText(e.target.value)}
            placeholder="e.g. move skid steer from mason creek to shop, pick up rental excavator from kelly tractor ft myers take to farm"
            rows={4}
            style={{
              width: '100%',
              padding: 12,
              borderRadius: 8,
              border: '2px solid #e5e7eb',
              fontSize: 16,
              fontFamily: 'inherit',
              resize: 'vertical',
              boxSizing: 'border-box'
            }}
          />
        </div>

        {/* Format Button */}
        <button
          onClick={handleFormat}
          disabled={loading || !inputText.trim()}
          style={{
            width: '100%',
            padding: '14px',
            borderRadius: 10,
            border: 'none',
            background: loading || !inputText.trim() ? '#d1d5db' : 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
            color: 'white',
            fontSize: 16,
            fontWeight: 700,
            cursor: loading || !inputText.trim() ? 'not-allowed' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 8,
            marginBottom: 16
          }}
        >
          {loading ? (
            <>
              <Loader2 size={20} style={{ animation: 'spin 1s linear infinite' }} />
              Processing...
            </>
          ) : (
            <>
              <Send size={20} />
              Format Dispatch
            </>
          )}
        </button>

        {/* Error */}
        {error && (
          <div style={{
            padding: 12,
            borderRadius: 8,
            backgroundColor: '#fef2f2',
            border: '1px solid #fecaca',
            color: '#dc2626',
            fontSize: 14,
            marginBottom: 16
          }}>
            {error}
          </div>
        )}

        {/* Formatted Result */}
        {formattedMessage && (
          <div style={{ marginBottom: 16 }}>
            <label style={{ fontSize: 14, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 6 }}>
              Formatted Dispatch
            </label>
            <div style={{
              padding: 16,
              borderRadius: 8,
              backgroundColor: 'white',
              border: '2px solid #8b5cf6',
              fontSize: 15,
              lineHeight: 1.6,
              whiteSpace: 'pre-wrap',
              fontFamily: 'inherit',
              color: '#1e293b'
            }}>
              {formattedMessage}
            </div>

            {/* Action Buttons */}
            <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
              <button
                onClick={handleShare}
                style={{
                  flex: 1,
                  padding: '12px',
                  borderRadius: 10,
                  border: 'none',
                  background: shared ? '#059669' : '#1e293b',
                  color: 'white',
                  fontSize: 15,
                  fontWeight: 700,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 8
                }}
              >
                {shared ? (
                  <>
                    <CheckCircle2 size={20} />
                    Shared
                  </>
                ) : (
                  <>
                    <Share2 size={20} />
                    Share to Driver
                  </>
                )}
              </button>
              <button
                onClick={handleCopy}
                style={{
                  padding: '12px 16px',
                  borderRadius: 10,
                  border: '1px solid #d1d5db',
                  background: copied ? '#d1fae5' : 'white',
                  color: copied ? '#059669' : '#374151',
                  fontSize: 15,
                  fontWeight: 600,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6
                }}
              >
                {copied ? <CheckCircle2 size={18} /> : <Copy size={18} />}
                {copied ? 'Copied' : 'Copy'}
              </button>
            </div>

            {/* New Dispatch */}
            <button
              onClick={handleClear}
              style={{
                width: '100%',
                padding: '12px',
                borderRadius: 10,
                border: '1px solid #d1d5db',
                background: 'white',
                color: '#64748b',
                fontSize: 14,
                fontWeight: 500,
                cursor: 'pointer',
                marginTop: 8
              }}
            >
              New Dispatch
            </button>
          </div>
        )}
      </div>

      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};

export default EquipmentDispatch;
